import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export const EditGuestDialog = ({ open, onOpenChange, guest, onUpdateGuest, hotelVendors = [], existingGuests = [] }) => {
  const [loading, setLoading] = useState(false);
  
  // Calculate available rooms for selected hotel (excluding current guest)
  const getAvailableRooms = (hotelId) => {
    const hotel = hotelVendors.find(h => h.id === hotelId);
    if (!hotel || !hotel.rooms_booked) return null;
    
    const totalRooms = hotel.rooms_booked;
    const roomsAllocated = existingGuests
      .filter(g => g.id !== guest?.id && g.hotelId === hotelId && g.numberOfRooms)
      .reduce((sum, g) => sum + (parseInt(g.numberOfRooms) || 0), 0);
    
    return {
      total: totalRooms,
      allocated: roomsAllocated,
      available: totalRooms - roomsAllocated
    };
  };
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    side: 'Bride Side',
    guestCount: 1,
    rsvpStatus: 'pending',
    mealPreference: 'Vegetarian',
    transportRequired: false,
    transportDetails: '',
    notes: '',
    hotelId: '',
    checkInDate: '',
    checkOutDate: '',
    numberOfRooms: '',
    roomNumbers: ''
  });

  useEffect(() => {
    if (guest) {
      setFormData({
        name: guest.name || '',
        email: guest.email || '',
        phone: guest.phone || '',
        side: guest.side || 'Bride Side',
        guestCount: guest.guestCount || 1,
        rsvpStatus: guest.rsvpStatus || 'pending',
        mealPreference: guest.mealPreference || 'Vegetarian',
        transportRequired: guest.transportRequired || false,
        transportDetails: guest.transportDetails || '',
        notes: guest.notes || '',
        hotelId: guest.hotelId || '',
        checkInDate: guest.checkInDate || '',
        checkOutDate: guest.checkOutDate || '',
        numberOfRooms: guest.numberOfRooms || '',
        roomNumbers: guest.roomNumbers || ''
      });
    }
  }, [guest]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }
    
    // Validate guest count
    const guestCount = parseInt(formData.guestCount);
    if (isNaN(guestCount) || guestCount < 1) {
      toast.error('Guest count must be at least 1');
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      const selectedHotel = hotelVendors.find(h => h.id === formData.hotelId);
      const updatedGuest = {
        ...guest,
        ...formData,
        guestCount: guestCount,
        hotelName: selectedHotel?.name || '',
        hotelAddress: selectedHotel?.location?.address || '',
        hotelLocation: selectedHotel?.location ? {
          lat: selectedHotel.location.lat,
          lng: selectedHotel.location.lng
        } : null,
        hotelContact: selectedHotel?.phone || '',
        numberOfRooms: formData.numberOfRooms ? parseInt(formData.numberOfRooms) : null,
        roomNumbers: formData.roomNumbers || null
      };
      
      onUpdateGuest(updatedGuest);
      toast.success('Guest updated successfully!');
      
      setLoading(false);
      onOpenChange(false);
    }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-2xl font-bold gradient-text">Edit Guest</DialogTitle>
        </DialogHeader>

        <form 
          onSubmit={handleSubmit} 
          className="flex flex-col flex-1 min-h-0"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="space-y-3 overflow-y-auto pr-2 flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Guest name"
                  className="mt-1.5"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  className="mt-1.5"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="guest@email.com"
                className="mt-1.5"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="side">Side</Label>
                <Select value={formData.side} onValueChange={(value) => setFormData({ ...formData, side: value })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bride Side">Bride Side</SelectItem>
                    <SelectItem value="Groom Side">Groom Side</SelectItem>
                    <SelectItem value="Common">Common</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="guestCount">Guest Count</Label>
                <Input
                  id="guestCount"
                  type="number"
                  min="1"
                  value={formData.guestCount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty value while typing, validate on blur
                    if (value === '' || value === null || value === undefined) {
                      setFormData({ ...formData, guestCount: '' });
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue) && numValue >= 1) {
                        setFormData({ ...formData, guestCount: numValue });
                      } else if (value === '0' || value === '-') {
                        // Allow typing 0 or negative temporarily
                        setFormData({ ...formData, guestCount: value });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Validate and set to 1 if invalid when user leaves field
                    const value = parseInt(e.target.value);
                    if (isNaN(value) || value < 1) {
                      setFormData({ ...formData, guestCount: 1 });
                    }
                  }}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rsvp">RSVP Status</Label>
                <Select value={formData.rsvpStatus} onValueChange={(value) => setFormData({ ...formData, rsvpStatus: value })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="meal">Meal Preference</Label>
                <Select value={formData.mealPreference} onValueChange={(value) => setFormData({ ...formData, mealPreference: value })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vegetarian">Vegetarian</SelectItem>
                    <SelectItem value="Non-Vegetarian">Non-Vegetarian</SelectItem>
                    <SelectItem value="Vegan">Vegan</SelectItem>
                    <SelectItem value="Jain">Jain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Hotel - From Vendors */}
            <div>
              <Label htmlFor="hotel">Hotel (from vendors)</Label>
              <Select value={formData.hotelId || "none"} onValueChange={(value) => setFormData({ ...formData, hotelId: value === "none" ? "" : value })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="No accommodation needed" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No accommodation needed</SelectItem>
                  {hotelVendors.length > 0 && (
                    hotelVendors.map((hotel) => (
                      <SelectItem key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {hotelVendors.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">Add hotels in Vendors module first</p>
              )}
            </div>

            {/* Hotel Accommodation Details - Show only if hotel is selected */}
            {formData.hotelId && (
              <>
                {/* Check-in/Check-out */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="checkIn">Check-in Date</Label>
                    <Input
                      id="checkIn"
                      type="date"
                      value={formData.checkInDate}
                      onChange={(e) => setFormData({ ...formData, checkInDate: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkOut">Check-out Date</Label>
                    <Input
                      id="checkOut"
                      type="date"
                      value={formData.checkOutDate}
                      onChange={(e) => setFormData({ ...formData, checkOutDate: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                {/* Room Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="numberOfRooms">
                      Number of Rooms
                      {(() => {
                        const roomInfo = getAvailableRooms(formData.hotelId);
                        if (roomInfo) {
                          return (
                            <span className={`ml-2 text-xs ${roomInfo.available > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ({roomInfo.available} of {roomInfo.total} available)
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </Label>
                    <Input
                      id="numberOfRooms"
                      type="number"
                      min="1"
                      value={formData.numberOfRooms}
                      onChange={(e) => setFormData({ ...formData, numberOfRooms: e.target.value })}
                      placeholder="e.g., 2"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="roomNumbers">Room Numbers</Label>
                    <Input
                      id="roomNumbers"
                      value={formData.roomNumbers}
                      onChange={(e) => setFormData({ ...formData, roomNumbers: e.target.value })}
                      placeholder="e.g., 101, 102"
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Transport */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="transport"
                checked={formData.transportRequired}
                onCheckedChange={(checked) => setFormData({ ...formData, transportRequired: checked })}
              />
              <Label htmlFor="transport" className="cursor-pointer">Transport Required</Label>
            </div>

            {formData.transportRequired && (
              <div>
                <Label htmlFor="transportDetails">Transport Details</Label>
                <Input
                  id="transportDetails"
                  value={formData.transportDetails}
                  onChange={(e) => setFormData({ ...formData, transportDetails: e.target.value })}
                  placeholder="Pickup location, time, etc."
                  className="mt-1.5"
                />
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="gradient-primary" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                'Update Guest'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditGuestDialog;
